<template>
  <base-wrapper :loading="loading">
    <div class="space-y-4">
      <cashbill-summary-widget :meta="summary" :with-omzet="filter.is_omzet" />

      <CashbillStatusSummary :status-summary="statusSummary" />

      <div class="flex flex-grow flex-col gap-2 xl:flex-row xl:justify-end">
        <BaseInput label="Stockist">
          <OfficeSelectSearch fullwidth :shadow="false" :office-category-id="4" v-model="filter.office" @change="loadSimpleOrders" />
        </BaseInput>
        <mitra-period-select
          class="grid gap-4 xl:flex"
          with-label
          v-model="filter.mitra_period_id"
          @change="loadSimpleOrders"
        />
        <base-input v-if="!filter.is_omzet" label="Status Faktur">
          <base-select
            :shadow="false"
            :options="filterStatusOptions"
            expand
            v-model="filter.status"
            @change="loadSimpleOrders"
          />
        </base-input>
        <base-input
          label="Pencarian"
          type="text"
          :shadow="false"
          fullwidth
          :lg-fullwidth="false"
          :xl-fullwidth="false"
          placeholder="Pencarian nomor invoice atau kode mitra"
          v-model="filter.search"
          debounce
          @native-input="loadSimpleOrders"
        />
        <BaseDropdown id="cashbill-filter-dropdown" class="xl:order-first flex items-end" :top-class="false">
          <template #toggle="{ toggle }">
            <BaseButton color="white" fullwidth @click="toggle">
              <Icon icon="heroicons:funnel" />
              Filter ({{ filterCount }})
            </BaseButton>
          </template>

          <template #content>
            <div class="p-4 space-y-2">
              <div class="flex items-start gap-2">
                <base-checkbox
                  label="Omzet"
                  name="omzet"
                  v-model="filter.is_omzet"
                  @change="loadSimpleOrders"
                ></base-checkbox>
                <p class="mt-1" title="Omzet adalah faktur yang sudah divalidasi kasir dan faktur tambah point yang sudah divalidasi kasir di periode berikutnya">
                  <Icon icon="heroicons:information-circle-solid" class="size-4 text-blue-600" />
                </p>
              </div>
              <template v-if="!filter.is_omzet">
                <base-checkbox
                  label="Registrasi Mitra Usaha"
                  name="registration"
                  v-model="filter.is_for_registration"
                  @change="loadSimpleOrders"
                ></base-checkbox>
                <base-checkbox
                  label="Aktivasi Mitra Usaha Expired"
                  name="expiry"
                  v-model="filter.is_for_office_expiry_activation"
                  @change="loadSimpleOrders"
                ></base-checkbox>
                <base-checkbox name="point" label="Tambah Point" v-model="filter.is_prev_bonus" @change="loadSimpleOrders"></base-checkbox>
              </template>
            </div>
          </template>
        </BaseDropdown>
      </div>
      <datatable
        :columns="tableColumns"
        :total="simpleOrders.meta.page.total"
        :perPage="simpleOrders.meta.page.perPage"
        :currentPage="simpleOrders.meta.page.currentPage"
        compact
        @pagechanged="onChangePage"
      >
        <template #tbody="{ classes }">
          <tr
            v-for="simpleOrder in simpleOrders.data"
            :key="simpleOrder.id"
            :class="[classes.tr, 'cursor-pointer bg-white hover:bg-green-100']"
            :title="getSingleIncluded(simpleOrders, simpleOrder.relationships.order.data.id).attributes.is_for_registration ? 'Pendaftaran Mitra Usaha' : ''"
            @click="onClickRow(simpleOrder)"
          >
            <td :class="classes.td">
              <p>
                <span class="font-bold text-gray-900">{{
                  simpleOrder.attributes.origin_code
                }}</span>
                /
                <span>{{ simpleOrder.attributes.destination_code }}</span>
              </p>
              <p class="text-xs">
                {{ simpleOrder.attributes.createdAt | formatDate }}
              </p>
            </td>
            <td :class="classes.td">
              <p class="font-bold text-gray-900 flex items-center gap-1">
                <Icon v-if="getSingleIncluded(simpleOrders, simpleOrder.relationships.order.data.id).attributes.is_for_registration" icon="heroicons:information-circle-solid" class="size-4 text-yellow-500" />
                {{
                  getSingleIncluded(
                    simpleOrders,
                    simpleOrder.relationships['destination-office'].data.id
                  ).attributes.code
                }}
              </p>
              <p class="text-xs">
                {{
                  getSingleIncluded(
                    simpleOrders,
                    simpleOrder.relationships['destination-office'].data.id
                  ).attributes.name
                }}
              </p>
            </td>
            <td :class="[classes.td, 'text-right']">
              {{
                getSingleIncluded(
                  simpleOrders,
                  simpleOrder.relationships.order.data.id
                ).attributes.grand_total_pv | toCurrency
              }}
            </td>
            <td :class="[classes.td, 'text-right']">
              {{
                getSingleIncluded(
                  simpleOrders,
                  simpleOrder.relationships.order.data.id
                ).attributes.total_bonus_value | toCurrency
              }}
            </td>
            <td :class="classes.td">
              <p class="text-gray-900">
                {{
                  getSingleIncluded(
                    simpleOrders,
                    simpleOrder.relationships['destination-office'].data.id
                  ).attributes.referred_by
                }}
              </p>
              <p class="text-xs">
                {{
                  getSingleIncluded(
                    simpleOrders,
                    simpleOrder.relationships['destination-office'].data.id
                  ).attributes.referred_by_name
                }}
              </p>
            </td>
            <td :class="[classes.td, 'text-right']">
              {{
                getSingleIncluded(
                  simpleOrders,
                  simpleOrder.relationships.order.data.id
                ).attributes.final_price | toCurrency
              }}
            </td>
            <td :class="[classes.td, 'text-center']">
              <base-badge
                :color="
                  getSingleIncluded(
                    simpleOrders,
                    simpleOrder.relationships.order.data.id
                  ).attributes.is_bonus_reported
                    ? 'green'
                    : 'red'
                "
                >{{
                  getSingleIncluded(
                    simpleOrders,
                    simpleOrder.relationships.order.data.id
                  ).attributes.is_bonus_reported
                    ? 'Dilaporkan'
                    : 'Belum Dilaporkan'
                }}</base-badge
              >
            </td>
            <td :class="[classes.td, 'text-center']">
              <base-badge color="indigo">{{
                simpleOrder.attributes.order_status
              }}</base-badge>
            </td>
          </tr>
        </template>
      </datatable>
    </div>
    <view-order-modal
      label="Cashbill"
      :columns="[
        'code',
        'warehouse',
        'office',
        'date',
        'user',
        'area',
        'shipment_type',
        'address',
      ]"
      :with-shipment="false"
      edit-route-name="transaksi-cashbill.edit"
      :visible="viewModal.visible"
      :order-id="viewModal.orderId"
      printable
      view-as="stockist-cashbill"
      readonly
      @close="viewModal.visible = false"
    />
  </base-wrapper>
</template>

<script>
import CashbillSummaryWidget from '@/components/cash-bill/cashbill-summary-widget.vue';
import MitraPeriodSelect from '@/components/period/mitra-period/mitra-period-select.vue';
import BaseCheckbox from '@/components/base/BaseCheckbox.vue';
import ViewOrderModal from '@/components/order/view-order-modal.vue';
import { mapGetters, mapActions } from 'vuex';
import { requestMixin } from '@//mixins/request/request';
import CashbillStatusSummary from '../../../../components/cash-bill/cashbill-status-summary.vue';
import BaseDropdown from '../../../../components/base/BaseDropdown.vue';
import OfficeSelectSearch from '../../../../components/office/office-select-search.vue';

export default {
  mixins: [requestMixin],
  components: {
    CashbillSummaryWidget,
    MitraPeriodSelect,
    BaseCheckbox,
    ViewOrderModal,
    CashbillStatusSummary,
    BaseDropdown,
    OfficeSelectSearch
  },
  data() {
    return {
      filter: {
        is_omzet: false,
        is_for_office_expiry_activation: false,
        is_for_registration: false,
        is_prev_bonus: false,
        mitra_period_id: null,
        status: null,
        search: null,
        office: {
          attributes: {},
        }
      },
      loading: false,
      simpleOrders: {
        data: [],
        meta: {
          page: {},
        },
      },
      viewModal: {
        visible: false,
        orderId: null,
      },
      summary: {
        totalPrice: 0,
        totalPv: 0,
        totalBv: 0,
        totalOmzet: 0,
        count: 0
      },
      statusSummary: {
        draft: 0,
        not_paid: 0,
        not_packed: 0,
        not_shipped: 0,
        not_taken: 0,
        shipped: 0,
        taken: 0
      },
    };
  },
  computed: {
    ...mapGetters({
      me: 'auth/getUser',
    }),
    filterCount() {
      if (this.filter.is_omzet) {
        return 1
      }

      return [
        this.filter.is_prev_bonus ? 1 : 0,
        this.filter.is_for_registration ? 1 : 0,
        this.filter.is_for_office_expiry_activation ? 1 : 0
      ].reduce((a, b) => a + b)
    },
    filterStatusOptions() {
      return [
        {
          key: 'null',
          value: null,
          label: 'Semua',
        },
        {
          key: 'true',
          value: true,
          label: 'Valid',
        },
        {
          key: 'reported',
          value: 'reported',
          label: 'Dilaporkan',
        },
        {
          key: 'false',
          value: false,
          label: 'Draft',
        },
        {
          key: 'cancel',
          value: 'cancel',
          label: 'Cancel',
        },
        {
          key: 'deleted',
          value: 'deleted',
          label: 'Hapus',
        },
      ];
    },
    filterStatus() {
      if (this.filter.is_omzet) {
        return {
          'filter[not-order_status]': 'Draft,Diproses Kasir',
        };
      }

      if (this.filter.status === null) {
        return {};
      }

      if (this.filter.status === 'deleted') {
        return {
          'filter[only-trahsed]': true,
        };
      }

      if (this.filter.status === 'reported') {
        return {
          'filter[order_status]': 'Bonus Terlaporkan',
        };
      }

      if (this.filter.status === 'cancel') {
        return {
          'filter[order_status]': 'Batal Faktur Gudang,Batal Faktur Kasir',
        };
      }

      if (this.filter.status) {
        return {
          'filter[not-order_status]': 'Draft',
        };
      }

      return {
        'filter[order_status]': 'Draft',
      };
    },
    tableColumns() {
      return [
        { id: 'code', name: 'No. Cashbill' },
        { id: 'destination', name: 'Mitra Usaha' },
        { id: 'pv', name: 'PV', theadClass: 'text-right' },
        { id: 'bv', name: 'BV', theadClass: 'text-right' },
        { id: 'upline', name: 'Upline' },
        {
          id: 'total_price',
          name: 'Total Harga Mitra Usaha',
          theadClass: 'text-right',
        },
        {
          id: 'report_status',
          name: 'Status Laporan',
          theadClass: 'text-center',
        },
        { id: 'status', name: 'Status Faktur', theadClass: 'text-center' },
      ];
    },
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert'
    }),
    async loadSimpleOrders(params, { withSummary } = { withSummary: true }) {
      this.loading = true;

      const [res, err] = await this.request(`/api/v1/orders`, {
        params: {
          'filter[origin_office_id]': this.filter.office?.attributes?.office_id,
          'filter[mitra_period_uuid]': this.filter.mitra_period_id,
          'filter[search]': this.filter.search,
          'filter[is_for_office_expiry_activation]':
            this.filter.is_omzet ? null : this.filter.is_for_office_expiry_activation,
          'filter[is_for_registration]':
            this.filter.is_omzet || !this.filter.is_for_registration ? null : true,
          'filter[is_prev_bonus]': this.is_omzet ? null : this.filter.is_prev_bonus ? true : null,
          'filter[destination_office_category_id]': 5,
          'filter[with_summary_cashbill]': withSummary,
          include: 'destination-office,order',
          'fields[simple-orders]':
            'origin_code,destination_code,createdAt,order_status,destination-office,order',
          'fields[offices]': 'code,name,referred_by,referred_by_name',
          'fields[orders]': 'grand_total_pv,total_bonus_value,final_price,is_for_registration,is_bonus_reported',
          'page[size]': 5,
          'with_total_omzet': this.filter.is_omzet,
          ...this.filterStatus,
          ...params,
        },
      });

      if (!err) {
        this.simpleOrders = res;

        if (withSummary) {
          this.summary.totalPrice = res.meta.total_price
          this.summary.totalPv = res.meta.total_pv
          this.summary.totalBv = res.meta.total_bv
          this.summary.count = res.meta.page.total
          this.summary.totalOmzet = res.meta.total_omzet

          this.statusSummary = res.meta.status_summary
        }
      }

      this.loading = false;
    },
    onChangePage(page) {
      this.loadSimpleOrders({
        'page[number]': page,
      }, { withSummary: false });
    },
    onClickRow(simpleOrder) {
      this.viewModal.orderId = simpleOrder.relationships.order.data.id;
      this.viewModal.visible = true;
    },
    onRefresh() {
      this.viewModal.visible = false;
      this.loadSimpleOrders();
    },
  },
  created() {
    this.filter.mitra_period_id = this.me.current_period.id;

    this.loadSimpleOrders({}, { withSummary: true });
  },
};
</script>
